import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { Redirect, useRouteMatch } from 'react-router-dom';
import Loader from '@cpg-its/ads.common.loading-circle';
import Banner from '@cpg-its/ads.containers.banner';
import { TcsmStates } from '../../../../root/tcsm-model';
import { GroupsState } from '../groups/groups-model';
import { useDocumentDownloadState } from './useDocumentDownload';
import { downloadFileFromApi } from '../../../../global/helpers/file-helpers';
import { ArcRoutes } from '../../constants/routes';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { GroupsBillingProps } from '../../models/arc-epi-props';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { ApiEndpoint } from '../../../../global/constants/api-endpoints';
import { ProductSummary } from './document-download-models';
import { YesNoIndicators } from '../../../../global/models/api-model';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { DividerSection } from '../../../../global/components/layout/divider-section';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { replaceAll } from '../../../../global/helpers/string-helpers';
import { useSystemState } from '../../../../global/hooks/useSystemState';

const fileUrls = {
    medicalDentalEnrollForm: `${ApiEndpoint.mlps}/document/pdf_form/enrollment_form/group/`,
    medicalDentalEnrollFormDirectBill: `${ApiEndpoint.mlps}/document/pdf_form/enrollment_form_direct_bill/group/`,
    medicalDentalAnnualEnrollForm: `${ApiEndpoint.mlps}/document/pdf_form/ae_form/group/`,
    medicalDentalAnnualEnrollFormDirectBill: `${ApiEndpoint.mlps}/document/pdf_form/ae_form_direct_bill/group/`,
    businessUnitRoster: `${ApiEndpoint.mlps}/document/reports/accounts/group/`,
    employeeList: `${ApiEndpoint.mlps}/document/reports/group_covered_persons/group/`,
    statementData: `${ApiEndpoint.mlps}/document/reports/group_statement_data/`,
    ueiStatementData: `${ApiEndpoint.mlps}/document/reports/group_statement_data_uei/`,
    dentalClaimForm: `${ApiEndpoint.mlps}/document/static_general/cigna_dental_claim_form`,
    disabilityPlans: `${ApiEndpoint.mlps}/document/static_general/disability_admin_form/group/`,
    groupLife: `${ApiEndpoint.mlps}/document/static_general/glife_health_statement`,
    contract: `${ApiEndpoint.mlps}/document/static_group/group_life_contract/`,
};

const downloadFileNames = {
    employeeList: 'EmployeeList.xls',
    dentalClaimForm: 'CIGNADentalClaimForm.pdf',
    disabilityPlans: 'Administration-Form-for-Employer-paid-Disability-Plans.pdf',
    groupLife: 'GroupLifeHealthStatement.pdf',
};


export function DocumentDownload() {
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const groupsState = useSelector<TcsmStates, GroupsState>(state => state.groupsState);
  const loaded = useDocumentDownloadState();
  const token = useSelector<TcsmStates, string>(state => state.authenticatedState.accessToken);
  const groupSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.GroupsLanding}${ArcRoutes.GroupSearch}`);
  const routeMatch = useRouteMatch();
  const epiProps = useSelector<TcsmStates, GroupsBillingProps>(state => state.epiProps?.groupsBillingSettingsState?.epiContent);
  const groupId = useSelector<TcsmStates, number>(state => state.groupsState?.currentGroup?.group_id);
  const groupAnnualEnrollmentStatus = useSelector<TcsmStates, any>(state => state.groupsState?.groupAnnualEnrollmentStatus);
  const productSummary = useSelector<TcsmStates, ProductSummary>(state => state.documentDownload?.productSummary);
  const contractList = useSelector<TcsmStates, any>(state => state.documentDownload?.groupLifeContractList);
  const currentCycleState = useSystemState();
  const today = currentCycleState?.base_cycle_date ? new Date(currentCycleState.base_cycle_date) : new Date();
  today.setMonth(today.getMonth() + 1);
  const year = today.getFullYear();
  const month = today.getMonth();
  const yearString = year.toString().padStart(4, '0');
  const monthString = month.toString().padStart(2, '0');
  const statementDateString = currentCycleState?.current_statement_date ? replaceAll(currentCycleState?.current_statement_date, '-', '') : `${yearString}${monthString}01`;

  const buttonClasses = buttonStyles();

  const isWithinAeWindow = (() => {
    if (groupAnnualEnrollmentStatus) {
      const { first_enrollment_allowed, last_admin_change_allowed, last_enrollment_allowed, last_enrollment_allowed_nominal } = groupAnnualEnrollmentStatus;
      const toMoment = (date: any) => (date ? moment(String(date)) : null);

      const firstDate = toMoment(first_enrollment_allowed);
      const possibleLastdates = [
        toMoment(last_admin_change_allowed),
        toMoment(last_enrollment_allowed),
        toMoment(last_enrollment_allowed_nominal),
      ].filter(Boolean) as moment.Moment[];

      if (!firstDate || possibleLastdates.length === 0) return false;

      const lastDate = moment.max(possibleLastdates);
      const aEstart = firstDate.clone().subtract(1, 'day').startOf('day');
      const aEend = lastDate.clone().add(1, 'day').endOf('day');

      return moment().isBetween(aEstart, aEend, undefined, '[]');
    }
    return null;
  })();

  if (!groupsState?.currentGroup?.group_id) {
      return (
          <Redirect to={{
              pathname: groupSelect,
              state: {
                  actionLink: routeMatch.path,
              },
          }} />);
  }

  if (!loaded || loading || !currentCycleState) {
      return <Loader />;
  }

  const downloadFile = (url, fileName) => {
      setHasError(false);
      setLoading(true);
      downloadFileFromApi(
          url,
          token,
          fileName,
          ()=>{
              setLoading(false);
              setHasError(false);
          },
          ()=>{
              setHasError(true);
              setLoading(false);
          });
  };

  function RenderError(){
      if (!hasError) return null;
      return (
      <ContentSpacer>
          <Banner
              banner={{
                  type: 'error',
                  label: <p className='input-label'> {epiProps.documentDownloadError} </p>,
              }}
          />
      </ContentSpacer>);
  }

  return (
      <>
          <RenderError/>
          <PageHeader headerText={epiProps.documentDownloadTitle} description={epiProps.documentDownloadDescription} />
          <DividerSection removeDivider>
              <ul className={buttonClasses.linkButtonList}>
              {
                  (productSummary?.product_categories?.medical === YesNoIndicators.Yes || productSummary?.product_categories?.dental === YesNoIndicators.Yes) &&
                  <>
                      <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.medicalDentalEnrollForm}${groupId}`, `MedTrust_${year}_General_<${groupId}>.pdf`)}><PictureAsPdfIcon /><span>{epiProps?.medicalDentalFormButtonText}</span></button></li>
                      <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.medicalDentalEnrollFormDirectBill}${groupId}`, `MedTrust_${year}_DirectBill_<${groupId}>.pdf`)}><PictureAsPdfIcon /><span>{epiProps?.medicalDentalFormDirectBillButtonText}</span></button></li>
                      {
                        (isWithinAeWindow &&
                          <>
                            <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.medicalDentalAnnualEnrollForm}${groupId}`, `MedTrust_${Number(year) + 1}_General_<${groupId}>.pdf`)}><PictureAsPdfIcon /><span>{`Medical / Dental Enrollment Form - ${Number(year) + 1} Annual Enrollment`}</span></button></li>
                            <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.medicalDentalAnnualEnrollFormDirectBill}${groupId}`, `MedTrust_${Number(year) + 1}_DirectBill_<${groupId}>.pdf`)}><PictureAsPdfIcon /><span>{`Medical / Dental Enrollment Form - ${Number(year) + 1} Annual Enrollment, Direct Bill Participants`}</span></button></li>
                          </>
                        )
                      }
                      <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.dentalClaimForm}`, downloadFileNames.dentalClaimForm)}><PictureAsPdfIcon /><span>{epiProps?.dentalHealthFormButtonText}</span></button></li>

                  </>
              }
              {
                  productSummary?.product_categories?.group_life === YesNoIndicators.Yes &&
                  <>
                      <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.groupLife}`, downloadFileNames.groupLife)}><PictureAsPdfIcon /><span>{epiProps?.groupLifeFormButtonText}</span></button></li>

                  </>
              }

              <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.disabilityPlans}${groupId}`, downloadFileNames.disabilityPlans)}><PictureAsPdfIcon /><span>{epiProps?.disabilityFormButtonText}</span></button></li>
              <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.businessUnitRoster}${groupId}`, `BusinessUnitRoster_<${groupId}>.xls`)}><svg className='' aria-hidden='true' focusable='false'>
                  <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#excel-icon' />
              </svg><span>{epiProps?.businessRosterButtonText?.replace('GROUPID', groupId.toString())}</span></button></li>
              <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.employeeList}${groupId}`, downloadFileNames.employeeList)}><svg className='' aria-hidden='true' focusable='false'>
                  <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#excel-icon' />
              </svg><span>{epiProps?.employeeListButtonText}</span></button></li>
              <li><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.statementData}group/${groupId}/month/${statementDateString}`, `StatementData_${year}_${month}_ Group_<${groupId}>.xls`)}><svg className='' aria-hidden='true' focusable='false'>
                  <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#excel-icon' />
              </svg><span>{epiProps?.statementDataButtonText?.replace('GROUPID', groupId.toString())?.replace('YYYY', year.toString())?.replace('MM', month.toString())}</span></button></li>
              {
                  productSummary?.product_categories?.unemployment_insurance === YesNoIndicators.Yes &&
                  <>
                      <li><button
                          className={buttonClasses.linkButton}
                          type='button'
                          onClick={() =>
                              downloadFile(
                                  `${fileUrls.ueiStatementData}group/${groupId}/month/${statementDateString}`,
                                  `StatementData_UEI_${year}_${month}_ Group_<${groupId}>.xls`)}
                      ><PictureAsPdfIcon />
                          <span>{epiProps?.ueiButtonText?.replace('GROUPID', groupId.toString())?.replace('YYYY', year.toString())?.replace('MM', month.toString())}</span>
                      </button></li>

                  </>
              }

              </ul>
          </DividerSection>
          <DividerSection sectionText={epiProps?.glcContractTitle?.replace('GROUPID', groupId.toString())}>
            {
              (contractList?.DATA && contractList?.DATA?.length > 0) &&
              <ul className={buttonClasses.linkButtonList}>
                {contractList?.DATA.map(a => <li key={a[0]}><button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${fileUrls.contract}selecteddocument/${a[0]}/group/${groupId}`, a[0])}><PictureAsPdfIcon /><span>{a[0]}</span></button></li>)}
              </ul>
            }
          </DividerSection>
      </>
  );
}
